import useOpensStore from '@/stores/OpensStore';
import { z } from 'zod';

// Required fields schema
export const requiredOpensConfigSchema = z.object({
  proxy_selection_id: z.string().min(1, 'Proxy selection is required'),
  max_daily_messages: z
    .number()
    .min(1, 'Max daily messages must be at least 1')
    .max(1000, 'Max daily messages cannot exceed 1000'),
  min_lead_score: z
    .number()
    .min(-100, 'Min lead score must be at least -100')
    .max(100, 'Min lead score cannot exceed 100'),
  message_template: z.string().min(1, 'Message template is required'),
  opens_window: z.object({
    timezone: z.string().min(1, 'Timezone is required'),
    start_time: z
      .string()
      .min(1, 'Start time is required')
      .regex(
        /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
        'Start time must be in HH:MM format (24-hour)'
      ),
    end_time: z
      .string()
      .min(1, 'End time is required')
      .regex(
        /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
        'End time must be in HH:MM format (24-hour)'
      ),
  }),
});

// Optional fields schema
export const optionalOpensConfigSchema = z.object({
  followers_selection_filters: z
    .object({
      sort_by_fields: z
        .array(
          z.object({
            field: z.enum(['lead_score', 'created_at']),
            direction: z.union([z.literal(-1), z.literal(1)]),
          })
        )
        .optional(),
      min_followers: z.number().min(0).optional(),
      is_verified: z.boolean().optional(),
      is_private: z.boolean().optional(),
      include_bio_keywords: z.array(z.string()).optional(),
      exclude_bio_keywords: z.array(z.string()).optional(),
      ensure_valid_profile_pic: z.boolean().optional(),
      include_predicted_location: z
        .array(z.string())
        .refine(
          (values) => {
            const { locationCountryCodes } = useOpensStore.getState();
            return values.every((value) =>
              Object.prototype.hasOwnProperty.call(locationCountryCodes, value)
            );
          },
          {
            message: 'All predicted locations must be valid country codes',
          }
        )
        .optional(),
      exclude_predicted_location: z
        .array(z.string())
        .refine(
          (values) => {
            const { locationCountryCodes } = useOpensStore.getState();
            return values.every((value) =>
              Object.prototype.hasOwnProperty.call(locationCountryCodes, value)
            );
          },
          {
            message: 'All predicted locations must be valid country codes',
          }
        )
        .optional(),
      include_predicted_gender: z.array(z.enum(['M', 'F'])).optional(),
      exclude_predicted_gender: z.array(z.enum(['M', 'F'])).optional(),
      include_username_keywords: z.array(z.string()).optional(),
      exclude_username_keywords: z.array(z.string()).optional(),
      include_full_name_keywords: z.array(z.string()).optional(),
      exclude_full_name_keywords: z.array(z.string()).optional(),
    })
    .optional(),
  stage_config: z
    .object({
      message_generator: z
        .object({
          message_selection: z
            .object({
              enabled: z.boolean().optional(),
              selection_method: z.string().optional(),
              message_templates: z
                .array(
                  z.object({
                    id: z.string(),
                    text: z.string(),
                  })
                )
                .min(1, 'At least one message template is required')
                .optional(),
            })
            .optional(),
        })
        .optional(),
    })
    .optional(),
});

// Complete schema (required + optional)
export const opensConfigSchema = requiredOpensConfigSchema.merge(
  optionalOpensConfigSchema
);

// Validation helper function
export const validateOpensConfig = (data) => {
  try {
    return { success: true, data: opensConfigSchema.parse(data) };
  } catch (error) {
    return {
      success: false,
      errors:
        error.errors?.map((err) => ({
          field: err.path.join('.'),
          message: err.message,
        })) || [],
    };
  }
};

// Helper to compare values deeply, handling arrays and objects
const compareValues = (val1, val2) => {
  if (val1 === val2) return true;
  if (val1 == null || val2 == null) return val1 === val2;
  if (Array.isArray(val1) && Array.isArray(val2)) {
    if (val1.length !== val2.length) return false;
    return val1.every((item, index) => compareValues(item, val2[index]));
  }
  if (typeof val1 === 'object' && typeof val2 === 'object') {
    const keys1 = Object.keys(val1);
    const keys2 = Object.keys(val2);
    if (keys1.length !== keys2.length) return false;
    return keys1.every((key) => compareValues(val1[key], val2[key]));
  }
  return false;
};

// Check if form state has changes compared to stored config
export const hasConfigChanges = (config, formState) => {
  // If no config exists (first time setup), allow saving if form has meaningful data
  if (!config) {
    return Boolean(
      formState.proxySelectionId ||
        formState.maxDailyMessages > 0 ||
        formState.timezone ||
        formState.startTime ||
        formState.endTime ||
        formState.messageTemplates?.length > 0 ||
        formState.sortByFields?.length > 0 ||
        formState.minFollowers > 0 ||
        formState.isVerified !== null ||
        formState.isPrivate !== null ||
        formState.ensureValidProfilePic !== null ||
        formState.includeBioKeywords?.length > 0 ||
        formState.excludeBioKeywords?.length > 0 ||
        formState.includePredictedLocation?.length > 0 ||
        formState.excludePredictedLocation?.length > 0 ||
        formState.includePredictedGender?.length > 0 ||
        formState.excludePredictedGender?.length > 0 ||
        formState.includeUsernameKeywords?.length > 0 ||
        formState.excludeUsernameKeywords?.length > 0 ||
        formState.includeFullNameKeywords?.length > 0 ||
        formState.excludeFullNameKeywords?.length > 0
    );
  }

  // Compare individual fields that matter
  let hasChanged = false;

  // Required fields
  if (config.proxy_selection_id !== formState.proxySelectionId)
    hasChanged = true;
  if (config.max_daily_messages !== formState.maxDailyMessages)
    hasChanged = true;
  if (config.min_lead_score !== formState.minLeadScore) hasChanged = true;

  // Opens window
  if (config.opens_window?.timezone !== formState.timezone) hasChanged = true;
  if (config.opens_window?.start_time !== formState.startTime)
    hasChanged = true;
  if (config.opens_window?.end_time !== formState.endTime) hasChanged = true;

  // Followers selection filters
  const storedFilters = config.followers_selection_filters || {};
  if (
    !compareValues(storedFilters.sort_by_fields || [], formState.sortByFields)
  )
    hasChanged = true;
  if ((storedFilters.min_followers || 0) !== formState.minFollowers)
    hasChanged = true;

  // Boolean filters (null means unset)
  const storedIsVerified =
    typeof storedFilters.is_verified === 'boolean'
      ? storedFilters.is_verified
      : null;
  const storedIsPrivate =
    typeof storedFilters.is_private === 'boolean'
      ? storedFilters.is_private
      : null;
  const storedEnsureValidProfilePic =
    typeof storedFilters.ensure_valid_profile_pic === 'boolean'
      ? storedFilters.ensure_valid_profile_pic
      : null;

  if (storedIsVerified !== formState.isVerified) hasChanged = true;
  if (storedIsPrivate !== formState.isPrivate) hasChanged = true;
  if (storedEnsureValidProfilePic !== formState.ensureValidProfilePic)
    hasChanged = true;

  // Keyword arrays
  if (
    !compareValues(
      storedFilters.include_bio_keywords || [],
      formState.includeBioKeywords
    )
  )
    hasChanged = true;
  if (
    !compareValues(
      storedFilters.exclude_bio_keywords || [],
      formState.excludeBioKeywords
    )
  )
    hasChanged = true;
  if (
    !compareValues(
      storedFilters.include_predicted_location || [],
      formState.includePredictedLocation
    )
  )
    hasChanged = true;
  if (
    !compareValues(
      storedFilters.exclude_predicted_location || [],
      formState.excludePredictedLocation
    )
  )
    hasChanged = true;
  if (
    !compareValues(
      storedFilters.include_predicted_gender || [],
      formState.includePredictedGender
    )
  )
    hasChanged = true;
  if (
    !compareValues(
      storedFilters.exclude_predicted_gender || [],
      formState.excludePredictedGender
    )
  )
    hasChanged = true;
  if (
    !compareValues(
      storedFilters.include_username_keywords || [],
      formState.includeUsernameKeywords
    )
  )
    hasChanged = true;
  if (
    !compareValues(
      storedFilters.exclude_username_keywords || [],
      formState.excludeUsernameKeywords
    )
  )
    hasChanged = true;
  if (
    !compareValues(
      storedFilters.include_full_name_keywords || [],
      formState.includeFullNameKeywords
    )
  )
    hasChanged = true;
  if (
    !compareValues(
      storedFilters.exclude_full_name_keywords || [],
      formState.excludeFullNameKeywords
    )
  )
    hasChanged = true;

  // Message templates
  const storedTemplates =
    config.stage_config?.message_generator?.message_selection
      ?.message_templates || [];
  if (!compareValues(storedTemplates, formState.messageTemplates))
    hasChanged = true;

  return hasChanged;
};
